import React from 'react';

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them to your server, and displays a fallback UI instead of
 * the component tree that crashed.
 * 
 * This prevents "gray screen" issues on iOS by showing what went wrong.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Log to console for development
    console.error('🔴 ErrorBoundary caught an error:', errorId);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);

    // Get user agent and device info
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    // Prepare error payload
    const errorPayload = {
      errorId,
      timestamp: new Date().toISOString(),
      error: {
        message: error.toString(),
        stack: error.stack,
        name: error.name
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      context: {
        url: window.location.href,
        userAgent,
        isIOS,
        isSafari,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height
        }
      },
      user: this.getUserInfo()
    };

    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Send to server
    this.logErrorToServer(errorPayload);
  }

  getUserInfo() {
    try {
      // Get user info from localStorage if available
      const tenantId = localStorage.getItem('tenantId');
      const userId = localStorage.getItem('userId');
      return {
        tenantId: tenantId || 'unknown',
        userId: userId || 'unknown'
      };
    } catch (e) {
      return { tenantId: 'unknown', userId: 'unknown' };
    }
  }

  async logErrorToServer(errorPayload) {
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      
      await fetch(`${baseUrl}/api/errors/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorPayload),
        // Use keepalive to ensure request completes even if page unloads
        keepalive: true
      });

      console.log('✅ Error logged to server:', errorPayload.errorId);
    } catch (err) {
      console.error('❌ Failed to log error to server:', err);
      // Fallback: try to log to console at least
      console.error('Original error payload:', errorPayload);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            maxWidth: '500px',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h1 style={{ 
              color: '#dc3545', 
              marginBottom: '10px',
              fontSize: '24px'
            }}>
              Something Went Wrong
            </h1>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              We've encountered an error. The issue has been automatically reported to our team.
            </p>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginBottom: '20px',
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                  Error Details (Development Only)
                </summary>
                <div style={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  <p><strong>Error ID:</strong> {this.state.errorId}</p>
                  <p><strong>Message:</strong> {this.state.error.toString()}</p>
                  <p><strong>Stack:</strong></p>
                  <pre style={{ 
                    backgroundColor: '#fff', 
                    padding: '10px', 
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {this.state.error.stack}
                  </pre>
                </div>
              </details>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Go Home
              </button>
            </div>

            {this.state.errorId && (
              <p style={{ 
                marginTop: '20px', 
                fontSize: '12px', 
                color: '#6c757d' 
              }}>
                Error ID: {this.state.errorId}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;